<template>
  <div class="promo-card informer">
    <div class="promo-card__card bg-white">
      <div class="promo-card__actions">
        <div>
          <a-select :value="selectedLanguage" size="small" @change="handleLangChange" class="lang-select">
            <a-select-option v-for="lang in langOptions" :key="lang.value">
              {{ lang.name }}
            </a-select-option>
          </a-select>
        </div>
        <div>
          <a-button v-if="!isViewscreen" type="link" :disabled="!editPermission" @click="openEditPage"
            class="mr-3"><a-icon type="edit" class="text-primary font-size-16" />Edit</a-button>
          <a-checkbox v-if="isViewscreen" :checked="addToViewscreen" @change="handleViewscreenChange" />
        </div>
      </div>
      <div class="promo-card__content" @click="openFullInfoModal">
        <div class="mb-3" style="position: relative;">
          <div style="height: 132px;" :class="{ 'tech-informer': isTechInformer }">
            <img v-if="selectedImage" class="promo-card__image" :src="selectedImage.path" alt="Informer image"
              style="width: 100%; height: 132px;" />
            <NoImagePic class="promo-card__image" v-else />
          </div>
        </div>
        <div class="promo-card__left">
          <div class="mb-2 d-flex align-items-center">
            <div style="width: 68%;">
              <div class="promo-card--title">Name for internal usage</div>
              <div class="d-flex">
                <a-icon v-if="isTechInformer" type="warning" class="mr-2 warning-icon" />
                <span class="promo-card-title promo-card--text">{{ informerContext.name }}</span>
              </div>
            </div>
            <div>
              <div class="promo-card--title">Tag</div>
              <span class="promo-card-title promo-card--text" style="text-transform: capitalize;">{{
                informerContext.tag}}</span>
            </div>
          </div>
          <div class="mb-2">
            <div class="promo-card--title">Section</div>
            <div v-if="Array.isArray(informerContext.displays) && informerContext.displays.length"
              style="line-height: 1.8rem">
              <a-tag class="info-modal--tag" v-for="display in informerContext.displays" :key="display.id">
                {{ displays[display.id] }}
              </a-tag>
            </div>
            <div v-else style="line-height: 1.8rem">-</div>
          </div>
          <div class="mb-2">
            <div class="promo-card--title">Apps</div>
            <div style="line-height: 2.1rem">
              <a-tag style="margin-right: 15px; padding: 1px 8px; border-radius: 2px"
                v-for="app in informerContext.applications.slice(0, 2)" :key="app.id">
                {{ app.name }}
                <a-icon v-if="app.platform.toLowerCase() === 'android'" type="android" class="ml-1 text-success" />
                <a-icon v-if="app.platform.toLowerCase() === 'ios'" type="apple" class="ml-1 text-primary" />
                <a-icon v-if="app.platform.toLowerCase() === 'web'" type="global" class="ml-1 text-default" />
              </a-tag>
              <a-tag v-if="informerContext.applications.length > 2">
                ...
              </a-tag>
            </div>
          </div>
          <div v-if="informerContext.periods">
            <div class="promo-card--title">Show Period</div>
            <div style="line-height: 2.1rem">
              <a-tag style="padding: 1px 8px; border-radius: 2px"
                v-for="period in informerContext.periods.slice(0, 1)" :key="period.starts_at">
                {{ `${formatDate(period.starts_at)} - ${formatDate(period.ends_at)} ${formatTimezone(period.time_mode)}` }}
              </a-tag>
              <a-tag v-if="informerContext.periods.length > 1" style="padding: 1px 8px; border-radius: 2px">
                {{ `+${informerContext.periods.length - 1}` }}
              </a-tag>
            </div>
          </div>
        </div>
      </div>
      <div class="promo-card__footer">
        <div class="d-flex align-items-center" :class="{ 'promo-card__footer--inf': !firstPeriodStart }">
          <div class="text-uppercase mr-auto">
            <span v-if="firstPeriodStart">
              {{ formatDate(firstPeriodStart) }} -
            </span>
            <span v-if="lastPeriodEnd">
              {{ formatDate(lastPeriodEnd) }}
            </span>
            <span v-else style="font-size: 20px;">&infin;</span>
          </div>
          <div class="d-flex">
            <a-switch :default-checked="informerContext.is_active" @change="informerActiveSwitch"
              :disabled="!editPermission" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// import apiClient from '@/services/axios'
import MediaService from '@/services/api/apiMediaService'
import NoImagePic from '@/views/apps/media/components/informers/noImagePic.vue'

import { formatTimezone } from '@/services/utils/media'

export default {
  name: 'Informer-card',
  components: {
    NoImagePic,
  },
  props: {
    informerInfo: {
      type: Object,
    },
    editPermission: {
      type: Boolean,
    },
    languages: {
      type: Array,
    },
    isViewscreen: {
      type: Boolean,
      default: false,
    },
    isMediaChecked: {
      type: Boolean,
      default: false,
    },
    displays: {
      type: Object,
    },
  },
  data: () => ({
    selectedLanguage: 'ru',
    addToViewscreen: false,
  }),
  computed: {
    informerContext() {
      return this.informerInfo.context
    },
    selectedInfo() {
      return this.informerContext.images[this.selectedLanguage]
    },
    selectedImage() {
      return this.selectedInfo?.image || null
    },
    langOptions() {
      const langKeys = Object.keys(this.informerContext.images)
      return this.languages.filter(language => langKeys.includes(language.name)).map(language => ({
        name: language.desc,
        value: language.name,
      }))
    },
    isTechInformer() {
      return this.informerInfo.tag === 'maintenance'
    },
    firstPeriodStart() {
      if (!this.informerContext?.periods?.length) {
        return null
      }

      return this.informerContext.periods[0].starts_at
    },
    lastPeriodEnd() {
      if (!this.informerContext?.periods?.length) {
        return null
      }

      const lastIndex = this.informerContext.periods?.length - 1

      return this.informerContext.periods[lastIndex].ends_at
    },
  },
  watch: {
    isMediaChecked(value) {
      this.addToViewscreen = value
    },
  },
  created() {
    if (this.isMediaChecked) {
      this.addToViewscreen = true
    }
  },
  methods: {
    formatTimezone,
    openFullInfoModal() {
      this.$emit('openFullInfo')
    },
    openEditPage() {
      this.$emit('openEdit')
    },
    formatDate: function (date) {
      return this.$moment(date).format('DD.MM.YY HH:mm')
    },
    handleLangChange(selectedLangKey) {
      this.selectedLanguage = selectedLangKey
    },
    handleViewscreenChange(event) {
      this.addToViewscreen = event.target.checked
      const action = event.target.checked ? 'addToMediaArray' : 'deleteFromMediaArray'
      this.$emit(action, this.informerContext.id)
    },
    async informerActiveSwitch(checked) {
      try {
        const informerPayload = {
          is_active: checked,
        }
        const informerResponse = await MediaService.updateInformer(this.informerContext.id, informerPayload)
        this.$notification.success({
          message: 'Active status changed',
          description: `${this.informerContext.name} status have been changed`,
        })
        if (informerResponse?.data?.error || informerResponse.data.success === false || informerResponse.data.error) {
          const errorMessage =
            informerResponse.data.error?.message ||
            'Something went wrong while updating informer activity'
          this.$notification.error({
            message: errorMessage,
          })
          this.loading = false
          return
        }
      } catch (e) {
        const errorMessage =
          e.response.data?.error?.message ||
          'Something went wrong while updating activity'
        this.$notification.error({
          message: errorMessage,
        })
      }
    },
  },
}
</script>

<style lang="scss" src="../style.module.scss" />
